import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import useHttp from '../../hooks/http.hook';
import '../business/Business.css';
import CreateBusinessHeader from '../business/components/CreateBusinessHeader';
import CreateFooter from '../business/components/CreateFooter';

function UpsertShipment() {
  const navigate = useNavigate();
  const location = useLocation();
  const { branchId, shipmentId } = useParams();
  const isEdit = !!shipmentId;

  const { loading, requestWithMeta } = useHttp();

  const [locationName, setLocationName] = React.useState('');
  const [price, setPrice] = React.useState('');
  const [submitError, setSubmitError] = React.useState(null);
  const [isPrefillLoading, setIsPrefillLoading] = React.useState(false);

  const listPath = `/branch/${branchId}/shipments/plans`;

  React.useEffect(() => {
    let cancelled = false;
    if (!isEdit) return;

    setSubmitError(null);

    async function prefill() {
      setIsPrefillLoading(true);
      try {
        const stateShipment = location?.state?.shipment ?? null;
        const stateId = stateShipment?.id ?? stateShipment?._id ?? null;
        if (stateShipment && stateId && String(stateId) === String(shipmentId)) {
          if (cancelled) return;
          setLocationName(stateShipment?.locationName ?? '');
          setPrice(
            stateShipment?.price === null || stateShipment?.price === undefined
              ? ''
              : String(stateShipment.price)
          );
          return;
        }

        if (!branchId || branchId === 'store') {
          if (cancelled) return;
          setSubmitError('Выберите филиал');
          return;
        }

        const res = await requestWithMeta(
          `/operator/branches/${encodeURIComponent(branchId)}/shipments`,
          'GET'
        );
        if (!res?.ok) {
          if (cancelled) return;
          setSubmitError(res?.data?.error || res?.data?.message || 'Не удалось загрузить доставку');
          return;
        }

        const list = Array.isArray(res?.data?.shipments) ? res.data.shipments : [];
        const shipment = list.find(
          (s) => String(s?.id ?? s?._id) === String(shipmentId)
        );
        if (!shipment) {
          if (cancelled) return;
          setSubmitError('Доставка не найдена');
          return;
        }

        if (cancelled) return;
        setLocationName(shipment?.locationName ?? '');
        setPrice(
          shipment?.price === null || shipment?.price === undefined
            ? ''
            : String(shipment.price)
        );
      } finally {
        if (!cancelled) setIsPrefillLoading(false);
      }
    }

    prefill();
    return () => {
      cancelled = true;
    };
  }, [isEdit, shipmentId, branchId, location?.state, requestWithMeta]);

  const parsedPrice = React.useMemo(() => {
    const n = parseFloat(String(price).replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  }, [price]);

  const isSubmitDisabled =
    loading ||
    isPrefillLoading ||
    locationName.trim().length === 0 ||
    parsedPrice === null ||
    parsedPrice < 0;

  async function handleSubmit() {
    setSubmitError(null);

    const trimmedName = locationName.trim();
    if (!trimmedName) {
      setSubmitError('Введите название локации');
      return;
    }
    if (parsedPrice === null || parsedPrice < 0) {
      setSubmitError('Введите корректную цену');
      return;
    }
    if (!branchId || branchId === 'store') {
      setSubmitError('Выберите филиал');
      return;
    }

    if (isEdit) {
      const res = await requestWithMeta(
        `/operator/shipments/${encodeURIComponent(shipmentId)}`,
        'PUT',
        { locationName: trimmedName, price: parsedPrice }
      );
      if (!res?.ok) {
        setSubmitError(res?.data?.error || res?.data?.message || 'Не удалось обновить доставку');
        return;
      }
      navigate(listPath, { replace: true });
      return;
    }

    const res = await requestWithMeta(
      `/operator/branches/${encodeURIComponent(branchId)}/shipments`,
      'POST',
      { locationName: trimmedName, price: parsedPrice }
    );
    if (!res?.ok) {
      setSubmitError(res?.data?.error || res?.data?.message || 'Не удалось создать доставку');
      return;
    }

    navigate(listPath, { replace: true });
  }

  return (
    <div className="shop-create-root">
      <div className="shop-create-mobile">
        <CreateBusinessHeader
          title={isEdit ? 'Редактировать доставку' : 'Новая доставка'}
          subtitle={
            isEdit
              ? 'Измените локацию и цену доставки'
              : 'Укажите локацию и цену доставки'
          }
          onBack={() => navigate(listPath)}
        />

        <main className="shop-create-content">
          {submitError ? (
            <section className="shop-create-section shop-create-sectionError">
              <div className="shop-create-label">Ошибка</div>
              <div className="shop-create-help shop-create-helpTight">{submitError}</div>
            </section>
          ) : null}

          <section className="shop-create-section">
            <label className="shop-create-label" htmlFor="shipment-location-name">
              Локация <b style={{ color: 'red' }}>*</b>
            </label>
            <input
              id="shipment-location-name"
              className="shop-create-input"
              placeholder="например, по городу, региону, за границу"
              value={locationName}
              onChange={(e) => setLocationName(e.target.value)}
              autoComplete="off"
              disabled={isPrefillLoading}
            />
          </section>

          <section className="shop-create-section">
            <label className="shop-create-label" htmlFor="shipment-price">
              Цена (сом) <b style={{ color: 'red' }}>*</b>
            </label>
            <input
              id="shipment-price"
              className="shop-create-input"
              type="number"
              inputMode="decimal"
              min="0"
              step="0.01"
              placeholder="например, 150"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              disabled={isPrefillLoading}
            />
          </section>
        </main>

        <CreateFooter
          disabled={isSubmitDisabled}
          label={
            loading
              ? isEdit
                ? 'Сохранение…'
                : 'Создание…'
              : isEdit
                ? 'Сохранить'
                : 'Создать доставку'
          }
          onCreate={handleSubmit}
        />
      </div>
    </div>
  );
}

export default UpsertShipment;
